/**
 * 竖屏抽屉切换 —— 窄视口下两个侧栏的互斥显示。
 *
 * 为什么需要它：左栏 230px + 右栏 296px + 边距 ≈ 620px，而竖屏手机可用宽度
 * 只有 375px。这是整套响应式里**唯一无法靠缩放解决**的一处 —— 其余（顶栏拥挤、
 * 侧栏溢出、建造栏消失）都是尺寸和上限问题，改 CSS 即可；两栏共存则是物理上
 * 放不下，必须改成二选一。
 *
 * 交互约定：
 * - 点未选中的页签 → 展开它，收起另一个
 * - 点已选中的页签 → 收起，露出下面的陨坑场景（竖屏下场景可视面积很珍贵）
 * - 选中某栋建筑 → 自动展开「建筑详情」，否则玩家点了建筑却看不到反馈
 *
 * 宽屏下 `#drawerTabs` 由媒体查询隐藏，两个 body class 不影响任何显示 ——
 * 本模块在宽屏是完全惰性的。
 */

type Drawer = 'left' | 'ins';

const CLS: Record<Drawer, string> = { left: 'drawer-left', ins: 'drawer-ins' };

let tabs: Record<Drawer, HTMLButtonElement> | null = null;

function apply(open: Drawer | null): void {
  const b = document.body;
  for (const d of ['left', 'ins'] as const) {
    b.classList.toggle(CLS[d], open === d);
    tabs?.[d].classList.toggle('on', open === d);
  }
}

function current(): Drawer | null {
  if (document.body.classList.contains(CLS.left)) return 'left';
  if (document.body.classList.contains(CLS.ins)) return 'ins';
  return null;
}

/** 供渲染层调用：选中建筑时把详情抽屉带出来。宽屏下无副作用。 */
export function revealInspector(): void {
  if (current() !== 'ins') apply('ins');
}

/** 供渲染层调用：进入放置模式时收起抽屉，把场景让出来给玩家选位置。 */
export function collapseDrawers(): void {
  if (current() !== null) apply(null);
}

/**
 * 弹性容器的**固有内容宽度**（与它当前被拉成多宽无关）。
 *
 * 不能用 `scrollWidth`：顶栏堆叠后会变成通栏（`left:14;right:14`），
 * `scrollWidth` 随之变成视口宽，判据于是永远成立 —— **一旦堆叠就再也回不去**。
 * 1920×1080 下顶栏被错误堆叠就是这个自锁造成的。
 *
 * 子项都是 `flex:none`，宽度不随容器变，因此累加子项 + 间隙 + 内边距
 * 得到的是真正的固有宽度。
 */
function intrinsicWidth(el: HTMLElement): number {
  const cs = getComputedStyle(el);
  const gap = parseFloat(cs.columnGap) || 0;
  const pad = (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0);
  const kids = Array.from(el.children) as HTMLElement[];
  const sum = kids.reduce((a, k) => a + k.offsetWidth, 0);
  return sum + gap * Math.max(0, kids.length - 1) + pad;
}

/**
 * 把建造栏的**实测**高度写进 `--dock-h`，侧栏的高度上限据此计算。
 *
 * 为什么不写死：我第一版按内容估了 118px，实测是 174px —— 侧栏因此和建造栏
 * 咬在一起。建造栏的高度受分类行是否换行、条目缩略图尺寸、图例是否折行影响，
 * 在断点之间会变，任何硬编码都只在写它的那一刻正确。
 *
 * 同一类错误在本项目出现过：g008 harness 的画布因为「假设加载时容器已有尺寸」
 * 而 0×0（见 G008 报告）。凡是「另一个元素多大」的问题，都要量而不是猜。
 */
function initHudMetrics(): void {
  const dock = document.getElementById('build');
  const topbar = document.getElementById('topbar');
  const clock = document.getElementById('clockbox');
  const logo = document.getElementById('logo');
  const minimap = document.getElementById('minimap');
  const tabsEl = document.getElementById('drawerTabs');
  if (!dock || !topbar || !clock) return;
  const logoW = logo ? logo.offsetWidth : 0; // 未堆叠时的快照，见下方 side 的说明

  const publish = (): void => {
    const css = document.documentElement.style;
    const h = Math.round(dock.getBoundingClientRect().height);
    if (h > 0) css.setProperty('--dock-h', `${h}px`);

    // 顶栏（居中）与时钟（右上）在窄屏必然重叠。阈值取决于顶栏的内容宽度，
    // 而顶栏的资源项会随 G012/G015 增加 —— 写死断点只在写它的那天成立。
    //
    // 顶栏是【居中】的，所以右侧时钟和左上 logo 都从两边**对称地**挤压它 ——
    // 较宽的那一侧必须算两次。少乘这个 2 是我第一版的错：算出 1175，
    // 而 1280 宽下实测重叠 109px（真实阈值 1509）。
    // logoW 必须用初始（未堆叠）时的快照：堆叠会隐藏 logo 使其宽度归零，
    // 若实时读取，宽 logo 场景下会「堆叠→变窄→判定不需堆叠→展开」来回横跳。
    const side = Math.max(clock.offsetWidth, logoW);
    const need = intrinsicWidth(topbar) + side * 2 + 44; // 44 = 左右边距 + 间隙
    document.body.classList.toggle('hud-stack', document.documentElement.clientWidth < need);
    // 顶栏在断点之间会换位（宽屏居中一行、窄屏降到时钟下方），高度也随字号变。
    // 取两者下沿的较大值再加呼吸，比任何硬编码都稳。
    // 紧凑模式下顶栏与时钟合成一条通栏：顶栏必须给时钟让出右侧宽度，
    // 否则资源项会滚到时钟底下。时钟宽度随「第 127 天」这类文案变化，仍是实测。
    css.setProperty('--clock-w', `${clock.offsetWidth}px`);
    css.setProperty('--clock-h', `${clock.offsetHeight}px`);
    // 窄到时钟几乎占满整行时，同一行的资源条会被压成几十像素（390px 竖屏实测
    // 只剩 42px，等于没有）。此时顶栏必须另起一行。阈值取「至少要放得下
    // 三四个资源项」，同样是实测而非写死断点。
    const RES_MIN = 260;
    document.body.classList.toggle('hud-rows',
      document.documentElement.clientWidth - clock.offsetWidth < RES_MIN);

    const top = Math.max(topbar.getBoundingClientRect().bottom, clock.getBoundingClientRect().bottom);
    const hudTop = Math.round(top) + 12;
    if (top > 0) css.setProperty('--hud-top', `${hudTop}px`);

    // 抽屉页签（仅竖屏可见）排在顶栏与面板之间，面板顶部顺延，否则页签会
    // 压住顶栏（390px 下实测重叠 20px）。
    const tabsH = tabsEl && getComputedStyle(tabsEl).display !== 'none'
      ? Math.round(tabsEl.getBoundingClientRect().height) + 8 : 0;
    const panelTop = hudTop + tabsH;
    css.setProperty('--panel-top', `${panelTop}px`);

    // 左栏与小地图共用左列，右栏下方只有建造栏。两者的可用高度不同：
    // 只扣建造栏会让左栏压住小地图（1366×768 实测咬合 4px）。
    const H = document.documentElement.clientHeight;
    const mm = minimap && getComputedStyle(minimap).display !== 'none'
      ? minimap.getBoundingClientRect().top : H - (dock.getBoundingClientRect().height || 0);
    css.setProperty('--left-max', `${Math.max(80, Math.round(mm) - panelTop - 12)}px`);
  };

  const ro = new ResizeObserver(publish);
  for (const el of [dock, topbar, clock]) ro.observe(el);
  // ResizeObserver 只报尺寸变化，不报位置变化；断点切换会挪动顶栏而尺寸不变
  addEventListener('resize', publish);
  publish();
}

export function initDrawers(): void {
  initHudMetrics();
  const left = document.getElementById('drwLeft') as HTMLButtonElement | null;
  const ins = document.getElementById('drwIns') as HTMLButtonElement | null;
  if (!left || !ins) return; // 页面没有抽屉条（harness 页面）就什么都不做
  tabs = { left, ins };
  for (const d of ['left', 'ins'] as const) {
    tabs[d].addEventListener('click', () => {
      apply(current() === d ? null : d); // 再点一次收起，让出场景
    });
  }
  // 初始【收起】：小屏最稀缺的是场景面积，一进来就被面板盖住是设计稿明确
  // 反对的形态。选中建筑时由 revealInspector() 自动带出详情，放置后再收起。
  apply(null);
}
